import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { fabric } from 'fabric';

@Component({
  selector: 'csi-signature-display',
  template: `
    <canvas #signatureCanvas></canvas>
  `,
  styles: [
    `
      canvas {
        border: 1px solid #ddd;
        background: white;
      }
    `
  ]
})
export class SignatureDisplayComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() signatureData: any;
  @ViewChild('signatureCanvas', { static: true }) canvasEl!: ElementRef<HTMLCanvasElement>;
  private canvas!: fabric.Canvas;
  private resizeListener = () => this.setCanvasSize();

  ngAfterViewInit() {
    this.setCanvasSize();
    this.canvas = new fabric.Canvas(this.canvasEl.nativeElement, {
      isDrawingMode: false,
      selection: false,
      enableRetinaScaling: true,
      preserveObjectStacking: true
    });

    window.addEventListener('resize', this.resizeListener);
    if (this.signatureData) this.loadSignature();
  }

  private setCanvasSize() {
    const container = this.canvasEl.nativeElement.parentElement;
    if (!container) return;

    const style = getComputedStyle(container);
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

    const maxWidth = 300;
    const width = Math.min(container.clientWidth - padX, maxWidth);
    const height = (width * 150) / 550;

    this.canvasEl.nativeElement.width = width;
    this.canvasEl.nativeElement.height = height;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.signatureData) {
      this.setCanvasSize();

      if (this.canvas) {
        this.loadSignature();
      }
    }
  }

  private loadSignature() {

    if (!this.signatureData || !this.canvas) {
      this.canvas && this.canvas.clear();
      return;
    }

    try {
      const data =
        typeof this.signatureData === 'string'
          ? JSON.parse(this.signatureData)
          : this.signatureData;
      this.loadCanvas(data);
    } catch (e) {
      console.error('Signature loading failed:', e);
      this.canvas.clear();
    }
  }

  private loadCanvas(json: any) {
    this.canvas.clear();
    this.canvas.loadFromJSON(json, () => {
      const objects = this.canvas.getObjects();
      if (objects.length > 0) {
        const boundingRect = objects[0].getBoundingRect(true);

        const width = boundingRect.width;
        const height = boundingRect.height;

        const scaleX = this.canvas.getWidth() / width;
        const scaleY = this.canvas.getHeight() / height;
        const scale = Math.min(scaleX, scaleY) * 0.85;

        this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        this.canvas.zoomToPoint(
          { x: this.canvas.getWidth() / 2, y: this.canvas.getHeight() / 2 },
          scale
        );
        this.canvas.viewportCenterObject(objects[0]);
      }

      objects.forEach(obj => {
        obj.set({
          fill: null,
          selectable: false,
          evented: false,
          strokeWidth: 2
        });
      });

      this.canvas.renderAll();
    });
  }
  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
    if (this.canvas) {
      this.canvas.dispose();
    }
  }
}
